import { useEffect, useCallback, memo, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AssessmentTable from "./AssesmentTable";
import PerformanceTable from "./PerformanceTable";
import ActionItemTable from "./ActionItemTable";
import P2PPageLayout from "./components/P2PPageLayout";
import P2PNavigationLinks from "./components/P2PNavigationLinks";
import MessageBanner from "./components/MessageBanner";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import type { RootState } from "../../store/store";
import type { BreadcrumbItem } from "../../components/common/BreadCrumbs";
import {
  setHasUnsavedChanges,
  setError,
  setValidationError,
  setSuccessMessage,
  fetchCategoryLevels,
  fetchP2PData,
  fetchTeamAggregatedData,
  saveP2PData,
  resetActionItems,
  resetAssessments,
  resetPerformanceData,
  updateTeamSettings,
} from "../../store/slices/pathToPerformanceSlice";
import Notifications from "../../components/common/Notifications";
import Modal from "../../components/common/Modal";
import Loader from "../../components/common/Loader";
import TableActionButtons from "../../components/common/TableActionButtons";
import {
  AUTHORIZATION_LEVEL,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  NOTIFICATION_MESSAGES,
  AUTO_DISMISS_DURATION,
} from "../../constants/pathToPerformance";
import { useUnsavedChangesPrompt } from "../../hooks/PathToPerformance/useUnsavedChangesPrompt";
import { useTeamIdInitialization } from "../../hooks/PathToPerformance/useTeamIdInitialization";
import { useMessageAutoDismiss } from "../../hooks/PathToPerformance/useMessageAutoDismiss";
import { useP2PDataMapping } from "../../hooks/PathToPerformance/useP2PDataMapping";
import {
  buildP2PSavePayload,
  buildTeamSettingsPayload,
} from "../../utils/PathToPerformance/p2pSaveHelpers";
import {
  hasP2PDataChanged,
  validateActionItems,
} from "../../utils/PathToPerformance/p2pValidation";
import { isValidDateFormat } from "../../utils/dateValidation";

const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 50% !important;
    width: 90% !important;
    top: 20% !important;
  }
`;

const PathPerformance = memo(() => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isResetting, setIsResetting] = useState(false);
  // Prevent provider rerenders from repeating the same initialization requests.
  const lastFetchKey = useRef<string | null>(null);
  // Inline date validation error for the Last Face-to-Face Assessment field
  const [dateError, setDateError] = useState<string | null>(null);

  // Get data from Redux store
  const {
    teamId,
    authorization,
    categoryLevels,
    teamData,
    p2pData,
    performanceData,
    initialPerformanceData,
    assessments,
    actionItems,
    initialActionItems,
    actionNeededEdited,
    initialAssessments,
    hasUnsavedChanges,
    loadingAuth,
    loadingP2PData,
    loadingCategoryLevels,
    loadingTeamData,
    savingP2P,
    savingTeamSettings,
    error,
    validationError,
    successMessage,
  } = useAppSelector((state: RootState) => state.pathToPerformance);

  // Initialize team ID from location state or session storage
  useTeamIdInitialization();
  useP2PDataMapping();

  // Handle unsaved changes navigation blocking
  const {
    showLeaveModal,
    handleNavigation,
    handleLeavePage,
    handleCancelLeave,
  } = useUnsavedChangesPrompt(hasUnsavedChanges, () => {
    dispatch(setHasUnsavedChanges(false));
  });

  /**
   * Build breadcrumb items for navigation
   * UBT Home → [Team Name] Projects → Path To Performance
   */
  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const items: BreadcrumbItem[] = [
      {
        label: location.state?.from ?? "UBT Home",
        onClick: () => handleNavigation(-1),
      },
    ];

    // Add project/team link if teamId and teamName are available
    if (teamId && teamData?.teamName) {
      items.push({
        label: `Project: ${teamData.teamName}`,
        path: `/home/projects`,
        state: { teamId, from: "Path To Performance" },
        title: `Project: ${teamData.teamName}`, // Full name for tooltip in case of long names
      });
    }

    // Add current page
    items.push({
      label: "Path To Performance",
    });

    return items;
  }, [location.state?.from, teamId, teamData?.teamName, handleNavigation]);

  /**
   * Scroll to top when component mounts or teamId changes
   * Ensures users always see the top content when landing on P2P page
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [teamId]);

  /**
   * Clear banner messages on component unmount
   * Prevents stale banners from displaying when navigating back to P2P page
   */
  useEffect(() => {
    return () => {
      dispatch(setError(null));
      dispatch(setValidationError(null));
      dispatch(setSuccessMessage(null));
    };
  }, [dispatch]);

  // Auto-dismiss success messages after 5 seconds
  useMessageAutoDismiss({
    message: successMessage,
    onDismiss: () => dispatch(setSuccessMessage(null)),
    duration: AUTO_DISMISS_DURATION.SUCCESS,
  });

  // Auto-dismiss API error messages after 5 seconds
  useMessageAutoDismiss({
    message: error,
    onDismiss: () => dispatch(setError(null)),
    duration: AUTO_DISMISS_DURATION.ERROR,
  });

  // Auto-scroll when validation error appears (never auto-dismiss)
  useMessageAutoDismiss({
    message: validationError,
    onDismiss: () => {}, // Validation errors are not auto-dismissed
    duration: AUTO_DISMISS_DURATION.VALIDATION,
    scrollToTop: true,
  });

  /**
   * Fetch data when authorization is granted
   */
  useEffect(() => {
    if (authorization && teamId) {
      // Only proceed if user has view or edit access
      if (
        authorization.authorizationLevel === AUTHORIZATION_LEVEL.EDIT ||
        authorization.authorizationLevel === AUTHORIZATION_LEVEL.VIEW
      ) {
        const fetchKey = `${teamId}:${authorization.authorizationLevel}`;
        // A new request is needed only when the selected team or access level changes.
        if (lastFetchKey.current === fetchKey) return;
        lastFetchKey.current = fetchKey;

        void dispatch(fetchCategoryLevels());
        void dispatch(fetchP2PData(teamId));
        void dispatch(fetchTeamAggregatedData(teamId));
      }
    }
  }, [authorization, teamId, dispatch]);

  /**
   * Check if any P2P data (action items / assessment notes) has changed
   */
  const hasChanges = useCallback((): boolean => {
    return hasP2PDataChanged(
      actionItems,
      initialActionItems,
      assessments,
      initialAssessments,
    );
  }, [actionItems, initialActionItems, assessments, initialAssessments]);

  /**
   * Unified save handler for the entire Path to Performance page.
   *
   * Validates the complete page before saving changed sections.
   */
  const handleSave = useCallback(async () => {
    if (!teamId) return;

    dispatch(setError(null));
    dispatch(setValidationError(null));
    dispatch(setSuccessMessage(null));
    setDateError(null);

    const originalDate = initialPerformanceData[0]?.lastFaceToFaceAssessment ?? "";
    const dateValidation = buildTeamSettingsPayload(
      teamId,
      performanceData,
      isValidDateFormat,
      originalDate,
    ).validation;
    if (!dateValidation.isValid) {
      setDateError(dateValidation.error ?? ERROR_MESSAGES.INVALID_DATE_FORMAT);
      return;
    }

    const p2pChanged = hasChanges();
    const performanceChanged = Boolean(
      performanceData[0] && initialPerformanceData[0] && (
        performanceData[0].usingHuddles !== initialPerformanceData[0].usingHuddles ||
        performanceData[0].lastFaceToFaceAssessment !== initialPerformanceData[0].lastFaceToFaceAssessment
      ),
    );

    // Compare against the original selections so a default-to-other-to-default
    // edit remains valid when the team originally had no rating.
    const requiresActionSelection =
      actionNeededEdited &&
      validateActionItems(initialActionItems) &&
      !validateActionItems(actionItems);

    // Validate explicit Action Needed edits before the no-changes shortcut.
    if (requiresActionSelection) {
      dispatch(setValidationError(ERROR_MESSAGES.NO_ACTION_SELECTED));
      return;
    }

    if (!p2pChanged && !performanceChanged) {
      dispatch(setSuccessMessage(SUCCESS_MESSAGES.NO_CHANGES));
      return;
    }

    if (performanceChanged) {
      const settingsResult = await dispatch(updateTeamSettings(
        buildTeamSettingsPayload(
          teamId,
          performanceData,
          isValidDateFormat,
          originalDate,
        ).payload,
      ));
      if (settingsResult.meta.requestStatus !== "fulfilled") {
        return;
      }
    }

    // Save P2P data when action items or assessment notes changed
    if (p2pChanged) {
      const payload = buildP2PSavePayload(
        teamId,
        actionItems,
        assessments,
        categoryLevels,
      );
      const isUpdate = !!p2pData?.current?.teamId;
      const p2pResult = await dispatch(saveP2PData({ data: payload, isUpdate }));
      if (p2pResult.meta.requestStatus !== "fulfilled") {
        return;
      }
    }

    dispatch(setError(null));
    dispatch(setValidationError(null));
    dispatch(setHasUnsavedChanges(false));
    dispatch(setSuccessMessage(SUCCESS_MESSAGES.P2P_SAVED));
    void dispatch(fetchTeamAggregatedData(teamId));
    await dispatch(fetchP2PData(teamId));
  }, [
    teamId,
    performanceData,
    initialPerformanceData,
    actionItems,
    initialActionItems,
    actionNeededEdited,
    assessments,
    categoryLevels,
    p2pData,
    dispatch,
    hasChanges,
  ]);

  /**
   * Unified reset handler — resets all editable sections of the page
   */
  const handleReset = useCallback(() => {
    dispatch(resetPerformanceData());
    dispatch(resetActionItems());
    dispatch(resetAssessments());
    dispatch(setHasUnsavedChanges(false));
    dispatch(setError(null));
    dispatch(setValidationError(null));
    dispatch(setSuccessMessage(null));
    setDateError(null);

    setIsResetting(true);
    setTimeout(() => setIsResetting(false), 200);
  }, [dispatch]);

  const canEdit =
    authorization?.authorizationLevel === AUTHORIZATION_LEVEL.EDIT;

  const isSaving = savingP2P || savingTeamSettings;
  const isButtonsDisabled =
    isSaving || loadingP2PData || loadingTeamData || loadingCategoryLevels;

  /**
   * Show "Please select UBT" notification
   */
  if (teamId === null && !loadingAuth) {
    return (
      <P2PPageLayout
        breadcrumbItems={breadcrumbItems}
        showScrollToTop={false}
      >
        <div className="mt-12! max-w-[80%]">
          <Notifications
            heading={NOTIFICATION_MESSAGES.SELECT_UBT_BEFORE_ACCESS}
            variant="Alert"
            messageInfo={ERROR_MESSAGES.NO_TEAM_SELECTED}
          >
            <ul className="flex items-start list-disc m-0 my-0! ml-4 mt-5!">
              <li>
                <Link
                  to="/home/select-ubt"
                  data-analytics-type="link"
                  state={
                    { from: "Find and Select UBT" } as Record<string, string>
                  }
                  className="text-kp-blue-dark underline text-left text-[16px]! w-full bg-transparent border-none p-0 cursor-pointer hover:text-kp-blue"
                  aria-label="Navigate to Select a UBT page"
                >
                  Select a UBT
                </Link>
              </li>
            </ul>
          </Notifications>
        </div>
      </P2PPageLayout>
    );
  }

  /**
   * Show loader while checking authorization
   */
  if (loadingAuth || !authorization) {
    return (
      <P2PPageLayout showScrollToTop={false}>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader />
        </div>
      </P2PPageLayout>
    );
  }

  /**
   * Show "Not authorized" notification
   */
  if (authorization.authorizationLevel === AUTHORIZATION_LEVEL.FORBIDDEN) {
    return (
      <P2PPageLayout breadcrumbItems={breadcrumbItems} showScrollToTop={false}>
        <div className="mt-12! max-w-[80%]">
          <Notifications
            variant="Alert"
            heading={ERROR_MESSAGES.UNAUTHORIZED}
          />
        </div>
      </P2PPageLayout>
    );
  }

  /**
   * Show loader while fetching initial data
   */
  const isLoadingData =
    loadingP2PData || loadingCategoryLevels || loadingTeamData;
  const hasMinimumData = p2pData !== null || error !== null;

  if (isLoadingData && !hasMinimumData) {
    return (
      <P2PPageLayout showScrollToTop={false}>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader />
        </div>
      </P2PPageLayout>
    );
  }

  return (
    <P2PPageLayout
      breadcrumbItems={breadcrumbItems}
      title="Path to Performance"
      navigationLinks={<P2PNavigationLinks teamId={teamId} />}
    >
      {/* Inline loader during save/fetch/reset operations */}
      {(isSaving || loadingP2PData || loadingTeamData || loadingCategoryLevels || isResetting) && (
        <div className="mt-8 flex justify-center">
          <Loader />
        </div>
      )}

      {!loadingCategoryLevels && !loadingTeamData && (
        <>
          {/* Save and Reset buttons — below the page heading */}
          {canEdit && (
            <TableActionButtons
              onSave={handleSave}
              onReset={handleReset}
              disabled={isButtonsDisabled}
              className="mb-8! "
            />
          )}

          {/* All page messages displayed below the buttons */}
          <MessageBanner
            error={error}
            validationError={validationError}
            successMessage={successMessage}
          />

          {teamData && (
            <h2 className="h2 mt-0! mb-4! whitespace-normal break-all">
              {teamData.teamName}
            </h2>
          )}

          {/* Performance Table Section */}
          <div className="mt-8">
            <PerformanceTable
              data={performanceData}
              readOnly={!canEdit}
              dateError={dateError}
              onDateFieldChange={() => setDateError(null)}
            />
          </div>

          {/* Assessment Table Section */}
          <div className="mt-12">
            <AssessmentTable data={assessments} readOnly={!canEdit} />
          </div>

          {/* Action Items Table Section */}
          <div className="mt-12">
            <ActionItemTable data={actionItems} readOnly={!canEdit} />
          </div>
        </>
      )}

      {/* Leave without saving modal */}
      <Modal
        isOpen={showLeaveModal}
        title="Leave without saving changes?"
        clearBtn="Cancel"
        submitBtn="Leave Page"
        onSecondary={handleCancelLeave}
        onPrimary={handleLeavePage}
        onClose={handleCancelLeave}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-12 h-12 shrink-0 mb-4!"
          />
          <p className="p3 -book mb-4!">
            {NOTIFICATION_MESSAGES.LEAVE_WITHOUT_SAVING}
          </p>
        </div>
        <style>{MODAL_STYLES}</style>
      </Modal>
    </P2PPageLayout>
  );
});

PathPerformance.displayName = "PathPerformance";

export default PathPerformance;
